import { makeStyles, Theme } from '@material-ui/core/styles'

/**
 * Styles for LeaseDocumentsRoot component
 */
type LeaseDocumentsRootStylesProps = {
    headerHeight: number
    collapsableSelectorHeight: number
}
export const useLeaseDocumentsRootStyles = makeStyles<
    Theme,
    LeaseDocumentsRootStylesProps
>((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: ({ headerHeight, collapsableSelectorHeight }) =>
            `calc(100vh - ${headerHeight + collapsableSelectorHeight}px)`,
        padding: theme.spacing(2),
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    statTiles: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    statTile: {
        minWidth: 140,
        padding: theme.spacing(1.5, 2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    statTileInteractive: {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[2],
        },
    },
    statTileActive: {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.action.selected,
        boxShadow: theme.shadows[2],
    },
    statLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    statValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
    },
    headerControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    actionButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
    segmentedControl: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 999,
        padding: theme.spacing(0.5),
        border: `1px solid ${theme.palette.divider}`,
        minWidth: 240,
    },
    segmentedThumb: {
        position: 'absolute',
        top: 4,
        bottom: 4,
        left: 4,
        width: 'calc(50% - 4px)',
        borderRadius: 999,
        backgroundColor: theme.palette.primary.main,
        transition: 'transform 250ms ease',
        zIndex: 0,
    },
    segmentButton: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.75, 1.5),
        borderRadius: 999,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        position: 'relative',
        zIndex: 1,
    },
    segmentActive: {
        color: theme.palette.common.white,
    },
    statsGrid: {
        marginBottom: theme.spacing(2),
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
        color: theme.palette.text.secondary,
    },
    emptyStateCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(4),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        maxWidth: 520,
        width: '100%',
    },
    emptyStateIcon: {
        fontSize: 44,
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '20',
        borderRadius: '50%',
        padding: theme.spacing(1.5),
    },
}))

export const useDocumentsTableViewStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 0,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    statusBadge: {
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.75),
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        backgroundColor: theme.palette.grey[100],
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        color: theme.palette.text.secondary,
    },
    tableContainer: {
        flex: 1,
        overflowY: 'auto',
    },
    tableSection: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    selectedRow: {
        backgroundColor: theme.palette.primary.light + '40',
        borderLeft: `3px solid ${theme.palette.primary.main}`,
    },
    unselectedRow: {
        '&:hover $selectButton': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

export const useDocumentParsingProgressStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3),
    },
    failureWrapper: {
        padding: theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
    },
    failureCard: {
        border: `1px solid ${theme.palette.common.black}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        maxWidth: 560,
        width: '100%',
        textAlign: 'center',
    },
    failureText: {
        color: theme.palette.common.black,
    },
    message: {
        marginBottom: theme.spacing(2),
    },
    stepSection: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    stepHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
    },
}))
